import type { IntegrationEntry } from '../../../../api/integrations-client/integrations-client.type';
import type { ExtendedSubproject } from './board-settings.type';

const UNKNOWN_SUBPROJECT_NAME = 'Unnamed board';
const UNKNOWN_PROJECT_NAME = 'Unnamed project';

/**
 * Collects and transforms subprojects from integration entries
 * @param {IntegrationEntry[]} integrationEntries Array of integration entries containing subprojects
 * @returns {ExtendedSubproject[]} Array of unique subprojects with added key property
 */
const collectSubprojects = (integrationEntries: IntegrationEntry[]): ExtendedSubproject[] => {
  const subprojectMap = new Map<string, ExtendedSubproject>();

  integrationEntries.forEach((entry) => {
    entry.subprojects.forEach((subproject) => {
      if (!subprojectMap.has(subproject.id)) {
        subprojectMap.set(subproject.id, {
          ...subproject,
          key: `${entry.id}-${subproject.id}`,
        });
      }
    });
  });

  return Array.from(subprojectMap.values());
};

/**
 * Groups subprojects by their project ID and name
 * @param {ExtendedSubproject[]} subprojects Array of subprojects to group
 * @returns {Record<string, { name: string; subprojects: ExtendedSubproject[] }>} Object with project IDs as keys and objects containing project name and subprojects array as values
 */
const getProjectGroups = (
  subprojects: ExtendedSubproject[],
): Record<string, { name: string; subprojects: ExtendedSubproject[] }> => {
  return subprojects.reduce(
    (acc, subproject) => {
      const projectId = subproject.project_id;
      const projectName = subproject.project_name ?? UNKNOWN_PROJECT_NAME;
      if (!acc[projectId]) {
        acc[projectId] = {
          name: projectName,
          subprojects: [],
        };
      }
      acc[projectId].subprojects.push(subproject);
      return acc;
    },
    {} as Record<string, { name: string; subprojects: ExtendedSubproject[] }>,
  );
};

/**
 * Returns true when the user has typed a non-empty name that differs from the
 * board setting's saved name. Used to enable the Save button as soon as the
 * name differs, without requiring the checkmark confirmation first.
 *
 * @param {string} editedName The name that the user has typed
 * @param {string | undefined} savedName The name that the board setting has saved
 * @returns {boolean} True when the user has typed a non-empty name that differs from the board setting's saved name, false otherwise
 */
const computeNameIsDirty = (editedName: string, savedName: string | undefined): boolean => {
  const trimmed = editedName.trim();
  return trimmed !== '' && trimmed !== (savedName ?? '').trim();
};

export { collectSubprojects, computeNameIsDirty, getProjectGroups, UNKNOWN_PROJECT_NAME, UNKNOWN_SUBPROJECT_NAME };
